import { Component, Input, Output, EventEmitter, inject, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { AppButton } from '../../../../../../shared/components/app-button/app-button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../../../core/services/settings.service';
import { Store } from '@ngrx/store';
import { selectBranchId } from '../../../../../../core/store/auth/auth.selectors';
import { firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';

/**
 * Create / edit a speciality, or a speciality group (isGroup = true). Both are
 * rows of specialties_tb; a plain speciality can optionally be placed in a
 * group at creation or edit time.
 */
@Component({
  selector: 'app-speciality-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    AppButton,
    TranslateModule
  ],
  templateUrl: './speciality-modal.html'
})
export class SpecialityModalComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private settingsService = inject(SettingsService);
  private store = inject(Store);
  private translate = inject(TranslateService);

  @Input() visible = false;
  @Input() speciality: any = null;
  /** True when creating/editing a group rather than a speciality. */
  @Input() isGroup = false;
  /** Groups of the branch, offered as the optional parent of a speciality. */
  @Input() groups: any[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  branchId: number | null = null;

  get title(): string {
    if (this.isGroup) return this.speciality ? 'speciality_group_edit' : 'speciality_group_new';
    return this.speciality ? 'speciality_edit' : 'speciality_new';
  }

  ngOnInit() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      group_id: [null as number | null]
    });

    this.store.select(selectBranchId).subscribe(id => this.branchId = id);
  }

  ngOnChanges() {
    if (this.visible && this.form) {
      if (this.speciality) {
        this.form.patchValue({
          name: this.speciality.name,
          group_id: this.speciality.group_id ?? null
        });
      } else {
        this.form.reset({ name: '', group_id: null });
      }
    }
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  async save() {
    if (this.form.invalid || !this.branchId) return;

    this.loading = true;
    const name = (this.form.value.name as string).trim();
    const groupId: number | null = this.isGroup ? null : this.form.value.group_id ?? null;

    try {
      if (this.speciality) {
        const patch: any = { name };
        if (!this.isGroup) patch.group_id = groupId;
        await firstValueFrom(this.settingsService.updateSpeciality(this.speciality.id, patch));
      } else {
        await firstValueFrom(
          this.settingsService.createSpeciality(name, this.branchId, true, { isGroup: this.isGroup, groupId })
        );
      }

      toast.success(this.translate.instant(this.isGroup ? 'speciality_group_saved' : 'speciality_saved'));
      this.saved.emit();
    } catch (err: any) {
      // specialties_name_branches_unique: one name per branch, shared between
      // specialities and groups. Say so instead of the generic failure.
      if (err?.code === '23505') {
        this.form.get('name')?.setErrors({ duplicate: true });
        this.form.get('name')?.markAsTouched();
        toast.error(this.translate.instant(
          this.isGroup ? 'speciality_group_name_duplicate' : 'speciality_name_duplicate',
          { name }
        ));
      } else {
        console.error(err);
        toast.error(this.translate.instant('speciality_error_save'));
      }
    } finally {
      this.loading = false;
    }
  }
}
